const hasThen = (value) => value && typeof value.then === 'function';
const DEFAULT_FIELD_SAVE_QUEUE_TIMEOUT_MS = 45_000;

export const FIELD_SAVE_QUEUE_TIMEOUT_CODE = 'FIELD_SAVE_QUEUE_TIMEOUT';
export const FIELD_SAVE_QUEUE_STATUS = Object.freeze({
    IDLE: 'idle',
    QUEUED: 'queued',
    RUNNING: 'running',
    STUCK: 'stuck',
});

const isFieldSaveQueueState = (value) => (
    value
    && typeof value === 'object'
    && hasThen(value.promise)
    && Object.prototype.hasOwnProperty.call(value, 'status')
);

const normalizeTimeoutMs = (timeoutMs) => (
    Number.isFinite(timeoutMs) && timeoutMs > 0
        ? timeoutMs
        : DEFAULT_FIELD_SAVE_QUEUE_TIMEOUT_MS
);

const createFieldSaveTimeoutError = (timeoutMs) => {
    const error = new Error(`Field save queue task timed out after ${timeoutMs}ms.`);
    error.name = 'FieldSaveQueueTimeoutError';
    error.code = FIELD_SAVE_QUEUE_TIMEOUT_CODE;
    error.timeoutMs = timeoutMs;
    error.isFieldSaveQueueTimeout = true;
    return error;
};

const readAbortError = (signal) => {
    if (!signal?.aborted) {
        return null;
    }
    return signal.reason instanceof Error
        ? signal.reason
        : new Error('Field save queue task was cancelled.');
};

const ensureFieldSaveQueueState = (queueRef) => {
    if (isFieldSaveQueueState(queueRef.current)) {
        return queueRef.current;
    }

    const initialPromise = hasThen(queueRef.current) ? queueRef.current : Promise.resolve();
    const state = {
        promise: initialPromise.catch(() => null),
        status: FIELD_SAVE_QUEUE_STATUS.IDLE,
        isStuck: false,
        pendingCount: 0,
        activeCount: 0,
        stuckAt: null,
        lastError: null,
        timeoutMs: null,
    };
    queueRef.current = state;
    return state;
};

const runTaskWithTimeout = (task, {
    timeoutMs,
    onStart,
    onTimeout,
    onFinish,
} = {}) => {
    const normalizedTimeoutMs = normalizeTimeoutMs(timeoutMs);
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timeoutId = null;
    let didTimeout = false;

    const taskPromise = Promise.resolve()
        .then(() => {
            onStart?.({ timeoutMs: normalizedTimeoutMs, signal: controller?.signal });
            return task({ signal: controller?.signal, timeoutMs: normalizedTimeoutMs });
        });

    const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
            didTimeout = true;
            const timeoutError = createFieldSaveTimeoutError(normalizedTimeoutMs);
            if (controller && typeof controller.abort === 'function') {
                try {
                    controller.abort(timeoutError);
                } catch (_) {
                    controller.abort();
                }
            }
            onTimeout?.(timeoutError);
            reject(timeoutError);
        }, normalizedTimeoutMs);
    });

    const publicPromise = Promise.race([taskPromise, timeoutPromise])
        .finally(() => {
            if (!didTimeout && timeoutId !== null) {
                clearTimeout(timeoutId);
            }
        });

    const queuePromise = taskPromise
        .finally(() => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
            onFinish?.({ didTimeout });
        });

    return {
        publicPromise,
        queuePromise,
    };
};

export function enqueueFieldSave(queueRef, task, { timeoutMs } = {}) {
    if (!queueRef || typeof queueRef !== 'object') {
        return Promise.reject(new Error('Cannot enqueue a field save without a queue.'));
    }
    if (typeof task !== 'function') {
        return Promise.reject(new Error('Cannot enqueue a field save without a task.'));
    }

    const queueState = ensureFieldSaveQueueState(queueRef);
    const previous = hasThen(queueState.promise) ? queueState.promise : Promise.resolve();
    queueState.pendingCount += 1;
    if (queueState.status === FIELD_SAVE_QUEUE_STATUS.IDLE) {
        queueState.status = FIELD_SAVE_QUEUE_STATUS.QUEUED;
    }

    let didStart = false;
    let didTimeout = false;
    const queuedRun = previous
        .catch(() => null)
        .then(() => runTaskWithTimeout(task, {
            timeoutMs,
            onStart: ({ timeoutMs: activeTimeoutMs }) => {
                didStart = true;
                queueState.pendingCount = Math.max(0, queueState.pendingCount - 1);
                queueState.activeCount += 1;
                queueState.status = FIELD_SAVE_QUEUE_STATUS.RUNNING;
                queueState.isStuck = false;
                queueState.stuckAt = null;
                queueState.lastError = null;
                queueState.timeoutMs = activeTimeoutMs;
            },
            onTimeout: (timeoutError) => {
                didTimeout = true;
                queueState.status = FIELD_SAVE_QUEUE_STATUS.STUCK;
                queueState.isStuck = true;
                queueState.stuckAt = Date.now();
                queueState.lastError = timeoutError;
            },
            onFinish: () => {
                if (didStart) {
                    queueState.activeCount = Math.max(0, queueState.activeCount - 1);
                } else {
                    queueState.pendingCount = Math.max(0, queueState.pendingCount - 1);
                }
                if (queueState.activeCount > 0) {
                    queueState.status = didTimeout
                        ? FIELD_SAVE_QUEUE_STATUS.STUCK
                        : FIELD_SAVE_QUEUE_STATUS.RUNNING;
                    return;
                }
                queueState.isStuck = false;
                queueState.stuckAt = null;
                queueState.status = queueState.pendingCount > 0
                    ? FIELD_SAVE_QUEUE_STATUS.QUEUED
                    : FIELD_SAVE_QUEUE_STATUS.IDLE;
            },
        }));

    const queuedSave = queuedRun.then(({ publicPromise }) => publicPromise);
    const queueTail = queuedRun.then(({ queuePromise }) => queuePromise);

    queueState.promise = queueTail.catch(() => null);
    return queuedSave;
}

export async function runWriteThenRefresh({
    write,
    refresh,
    onWriteSuccess,
    onWriteError,
    onRefreshError,
    signal,
} = {}) {
    if (typeof write !== 'function') {
        throw new Error('Cannot save field without a write function.');
    }

    const throwIfAborted = () => {
        const abortError = readAbortError(signal);
        if (abortError) {
            throw abortError;
        }
    };

    throwIfAborted();
    let writeResult;
    try {
        writeResult = await write({ signal });
    } catch (error) {
        if (!readAbortError(signal)) {
            onWriteError?.(error);
        }
        throw error;
    }
    throwIfAborted();
    onWriteSuccess?.(writeResult);

    if (typeof refresh !== 'function') {
        return;
    }

    throwIfAborted();
    try {
        await refresh({ signal });
    } catch (error) {
        if (!readAbortError(signal)) {
            onRefreshError?.(error);
        }
    }
    throwIfAborted();
}
